import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { 
  Save, 
  ArrowLeft,
  Package,
  Loader2,
  AlertCircle,
  CheckCircle,
  Hash,
  FileText,
  Calendar,
  Ban
} from 'lucide-react';
import { InBooks24Logo } from './InBooks24Logo';
import { grnApiService, GRN, GRNDetail } from '../services/grnApi';
import { purchaseOrderApiService } from '../services/purchaseOrderApi';
import { Branch, branchApiService } from '../services/branchApi';
import { storeApiService, Store } from '../services/storeApi';
import { rmRejectionApiService, RmRejectionRegisterDto } from '../services/rmRejectionApi';
import type { RawItem } from '../services/rawItemApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { formatQty3, isPartialQtyInput, parseQtyInput, resolveQtyFromInput, coerceQty3, isMachineryCategory, getRawItemCategoryName, qtyFromAmount, isPartialMoneyInput, parseMoneyInput, formatMachineryQty, isPartialMachineryQtyInput, parseMachineryQtyInput } from '../utils/quantityUtils';
import { formatIndianAmount, lineAmountFromQtyRate } from '../utils/taxUtils';

interface EditableGRNDetail extends Omit<GRNDetail, 'tcNo' | 'tcDate'> {
  originalReceivedQuantity: number;
  originalStoreId: number | null;
  originalNOP: number | null;
  isModified: boolean;
  /** Client-only row for a PO item not yet received on this GRN */
  isNew?: boolean;
  /** PO-level remaining qty (ordered − received across GRNs) */
  remainingQuantity?: number;
  tcDocumentPath?: string | null;
  tcDocFileName?: string | null; // Store original filename
  tcNo?: string | null; // Override to allow null
  tcDate?: string | null; // Override to allow null
}

function fallbackRawItem(id: number, name: string): RawItem {
  return {
    id,
    itemName: name || 'Unknown Item',
    categoryName: 'RAW MATERIAL',
    itemUnit: '',
    type: null,
    sku: '',
    color: '',
    shape: '',
    size: '',
    grade: '',
    weight: 0,
    minBufferStock: 0,
    applicableIsStd: null,
    isActive: true,
    createdBy: 0,
    updatedBy: 0,
    createdAt: '',
    updatedAt: '',
    itemCategory: undefined as unknown as RawItem['itemCategory'],
    hsn: undefined as unknown as RawItem['hsn'],
  };
}

export function GRNEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeOnlyMode = searchParams.get('mode') === 'store';
  const [grn, setGrn] = useState<GRN | null>(null);
  const [grnDetails, setGrnDetails] = useState<EditableGRNDetail[]>([]);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingItems, setSavingItems] = useState<Record<number, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedDetailForRejection, setSelectedDetailForRejection] = useState<EditableGRNDetail | null>(null);
  const [rejectionData, setRejectionData] = useState({
    rejectionDate: '',
    rejectionQty: '',
    rejectionReason: ''
  });
  const [savingRejection, setSavingRejection] = useState(false);
  const [rejectionQuantities, setRejectionQuantities] = useState<Record<number, number>>({});
  const [allRejections, setAllRejections] = useState<RmRejectionRegisterDto[]>([]);
  const [existingRejections, setExistingRejections] = useState<RmRejectionRegisterDto[]>([]);
  const [editingRejection, setEditingRejection] = useState<RmRejectionRegisterDto | null>(null);
  const [receivedQtyInputs, setReceivedQtyInputs] = useState<Record<number, string>>({});
  const [amountInputs, setAmountInputs] = useState<Record<number, string>>({});

  useEffect(() => {
    if (id) {
      loadGRNData();
    }
  }, [id]);

  const computeIsModified = (
    detail: EditableGRNDetail,
    overrides: Partial<Pick<EditableGRNDetail, 'receivedQuantity' | 'storeId' | 'nop'>> = {},
  ): boolean => {
    const receivedQuantity = overrides.receivedQuantity ?? detail.receivedQuantity;
    const storeId = overrides.storeId !== undefined ? overrides.storeId : detail.storeId;
    const nop = overrides.nop !== undefined ? overrides.nop : detail.nop;

    if (storeOnlyMode) {
      return (storeId ?? null) !== detail.originalStoreId;
    }

    if (detail.isNew) {
      return receivedQuantity > 0;
    }

    return (
      receivedQuantity !== detail.originalReceivedQuantity ||
      (storeId ?? null) !== detail.originalStoreId ||
      (nop ?? null) !== detail.originalNOP
    );
  };

  const loadGRNData = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const [grnData, detailsData] = await Promise.all([
        grnApiService.getGRNById(id!),
        grnApiService.getGRNDetailsByGRN(parseInt(id!))
      ]);
      
      setGrn(grnData);
      
      // Convert to editable format
      const editableDetails: EditableGRNDetail[] = detailsData.map(detail => ({
        ...detail,
        originalReceivedQuantity: detail.receivedQuantity,
        originalStoreId: detail.storeId || null,
        originalNOP: detail.nop ?? null,
        isModified: false,
        isNew: false,
        tcDocFileName: null, // Will be loaded if available from backend
        tcDocumentPath: (detail as any).tcDocumentPath || (detail as any).tcDoc || null
      }));

      let mergedDetails = editableDetails;
      const poHeaderId =
        grnData.poHeader?.id ??
        (grnData as GRN & { poHeaderId?: number }).poHeaderId;
      if (!storeOnlyMode && grnData.type === 'PO' && poHeaderId) {
        try {
          const poItems = await purchaseOrderApiService.getPoItemDetails(String(poHeaderId));
          if (Array.isArray(poItems)) {
            const existingRawItemIds = new Set(
              detailsData.map((d) => d.rawItem?.id).filter((rawId): rawId is number => Boolean(rawId))
            );
            const maxLineNumber = detailsData.reduce((max, d) => Math.max(max, d.lineNumber || 0), 0);
            const unreceivedRows: EditableGRNDetail[] = [];
            poItems.forEach((item, index) => {
              const rawItemId = item.rawItem?.id ?? item.rawItemId;
              const remainingQuantity = Math.max((item.quantity || 0) - (item.receivedQuantity || 0), 0);
              if (!rawItemId || existingRawItemIds.has(rawItemId) || remainingQuantity <= 0) {
                return;
              }
              existingRawItemIds.add(rawItemId);
              unreceivedRows.push({
                id: -(index + 1),
                lineNumber: maxLineNumber + unreceivedRows.length + 1,
                quantity: item.quantity || 0,
                receivedQuantity: 0,
                unitPrice: item.unitPrice || 0,
                lineTotal: 0,
                qualityCheck: '',
                remarks: '',
                heatNo: '',
                supplierHeatNo: '',
                nop: undefined,
                branchId: grnData.branchId,
                storeId: null,
                createdBy: grnData.createdBy,
                updatedBy: grnData.updatedBy,
                createdAt: '',
                updatedAt: '',
                grn: grnData,
                rawItem:
                  item.rawItem ??
                  fallbackRawItem(rawItemId, item.itemDescription || 'Unknown Item'),
                qualityStatus: '',
                qualityCheckComplete: false,
                originalReceivedQuantity: 0,
                originalStoreId: null,
                originalNOP: null,
                isModified: false,
                isNew: true,
                remainingQuantity,
              });
            });
            mergedDetails = [...editableDetails, ...unreceivedRows];
          }
        } catch {
          toast.error('Could not load remaining PO items for this GRN');
        }
      }

      setGrnDetails(mergedDetails);
      setReceivedQtyInputs({});
      setHasChanges(false);

      // Load rejection quantities for each detail
      try {
        const rejectionsResponse = await rmRejectionApiService.getAllRejections();
        if (rejectionsResponse.success && rejectionsResponse.data) {
          setAllRejections(rejectionsResponse.data);
          const rejectionQtyMap: Record<number, number> = {};
          
          // Group rejections by GRN detail (using heatNo and grnNumber)
          editableDetails.forEach(detail => {
            const detailRejections = rejectionsResponse.data.filter(rej => 
              rej.grnNumber === grnData.grnNumber &&
              rej.heatNo === detail.heatNo &&
              rej.rawItemId === detail.rawItem.id
            );
            const totalRejectionQty = detailRejections.reduce((sum, rej) => sum + (rej.rejectQty || 0), 0);
            rejectionQtyMap[detail.id] = totalRejectionQty;
          });
          
          setRejectionQuantities(rejectionQtyMap);
        }
      } catch (error) {
        // console.warn('Could not load rejection quantities:', error);
        // Set empty rejection quantities if fetch fails
        setRejectionQuantities({});
        setAllRejections([]);
      }
      
      // Load branch information if available
      if (grnData.branchId) {
        try {
          const branches = await branchApiService.getBranches();
          const branchData = branches.find(b => b.id === grnData.branchId);
          if (branchData) {
            setBranch(branchData);
          }
        } catch (error) {
          // console.warn('Could not load branch information:', error);
        }

        // Load stores for the branch
        try {
          const storesData = await storeApiService.getStoresByBranch(grnData.branchId);
          setStores(storesData);
        } catch (error) {
          // console.warn('Could not load stores:', error);
        }
      }
    } catch (error) {
      toast.error('Failed to load GRN details');
      if (!options?.silent) {
        navigate('/grn');
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const updateQuantity = (
    detailId: number,
    newQuantity: number,
    opts?: { fromAmount?: boolean; lineTotal?: number },
  ) => {
    if (storeOnlyMode) return;

    if (!opts?.fromAmount) {
      setAmountInputs((prevAmt) => {
        const { [detailId]: _, ...rest } = prevAmt;
        return rest;
      });
    } else {
      setReceivedQtyInputs((prevQty) => {
        const { [detailId]: _, ...rest } = prevQty;
        return rest;
      });
    }

    setGrnDetails(prev => {
      const next = prev.map(detail => {
        if (detail.id === detailId) {
          let qty = newQuantity;
          if (detail.isNew) {
            const remaining = coerceQty3(detail.remainingQuantity ?? detail.quantity);
            if (remaining <= 0 && qty > 0) {
              toast.error('This item is fully received. Cannot receive more.');
              return detail;
            }
            if (qty > remaining) {
              toast.error(
                `Received quantity cannot exceed remaining quantity (${formatQty3(remaining)})`,
              );
              return detail;
            }
          } else if (grn?.type === 'PO' && qty > detail.quantity) {
            toast.error('Received quantity cannot exceed PO quantity');
            return detail;
          }
          return {
            ...detail,
            receivedQuantity: qty,
            lineTotal:
              opts?.lineTotal !== undefined
                ? opts.lineTotal
                : lineAmountFromQtyRate(qty, detail.unitPrice),
            isModified: computeIsModified(detail, { receivedQuantity: qty }),
          };
        }
        return detail;
      });
      setHasChanges(next.some(d => d.isModified));
      return next;
    });
  };

  const updateAmount = (detailId: number, amount: number) => {
    if (storeOnlyMode) return;
    const detail = grnDetails.find((d) => d.id === detailId);
    if (!detail) return;
    if (!isMachineryCategory(getRawItemCategoryName(detail.rawItem))) return;
    if (!detail.unitPrice || detail.unitPrice <= 0) {
      toast.error('Unit price is missing; enter quantity instead');
      return;
    }
    updateQuantity(detailId, qtyFromAmount(amount, detail.unitPrice), {
      fromAmount: true,
      lineTotal: amount,
    });
  };

  const updateSupplierHeatNo = (detailId: number, supplierHeatNo: string) => {
    if (storeOnlyMode) return;
    setGrnDetails((prev) =>
      prev.map((detail) =>
        detail.id === detailId && detail.isNew ? { ...detail, supplierHeatNo } : detail
      )
    );
  };

  const updateStore = (detailId: number, storeId: number | null) => {
    setGrnDetails(prev => {
      const next = prev.map(detail => {
        if (detail.id === detailId) {
          const normalizedStoreId = storeId ?? null;
          return {
            ...detail,
            storeId: normalizedStoreId,
            isModified: computeIsModified(detail, { storeId: normalizedStoreId }),
          };
        }
        return detail;
      });
      setHasChanges(next.some(d => d.isModified));
      return next;
    });
  };

  const updateNOP = (detailId: number, newNOP: number) => {
    if (storeOnlyMode) return;

    setGrnDetails(prev => {
      const next = prev.map(detail => {
        if (detail.id === detailId) {
          const nopValue = newNOP > 0 ? newNOP : undefined;
          return {
            ...detail,
            nop: nopValue,
            isModified: computeIsModified(detail, { nop: nopValue }),
          };
        }
        return detail;
      });
      setHasChanges(next.some(d => d.isModified));
      return next;
    });
  };


  const validateQuantities = () => {
    const invalidDetails = grnDetails.filter(detail => {
      // Existing lines may be set to 0 to reverse a mistaken receipt; new lines still require qty > 0
      if (detail.isNew && detail.receivedQuantity <= 0) return true;
      if (!detail.isNew && detail.receivedQuantity < 0) return true;
      // For PO type GRNs, check against PO quantity; for MANUAL type, no upper limit
      if (grn?.type === 'PO' && detail.receivedQuantity > detail.quantity) return true;
      return false;
    });
    
    if (invalidDetails.length > 0) {
      const errorMessage = grn?.type === 'MANUAL' 
        ? 'Please ensure all quantities are valid (0 or greater for existing lines; greater than 0 for new lines)'
        : 'Please ensure all quantities are valid (0 or greater for existing lines; greater than 0 for new lines, and not exceeding PO quantity)';
      toast.error(errorMessage);
      return false;
    }
    
    return true;
  };

  const handleSaveItem = async (detailId: number) => {
    const detail = grnDetails.find(d => d.id === detailId);
    if (!detail) {
      toast.error('Item not found');
      return;
    }

    if (!detail.isModified) {
      toast.info('No changes to save for this item');
      return;
    }

    if (storeOnlyMode) {
      if ((detail.storeId ?? null) === detail.originalStoreId) {
        toast.info('No store changes to save for this item');
        return;
      }
      if (!detail.storeId) {
        toast.error('Please select a store');
        return;
      }
    } else {
      const isMachinery = isMachineryCategory(getRawItemCategoryName(detail.rawItem));
      const resolvedQty = resolveQtyFromInput(
        receivedQtyInputs[detailId],
        detail.receivedQuantity,
        isMachinery ? parseMachineryQtyInput : parseQtyInput,
      );
      if (detail.isNew) {
        if (resolvedQty <= 0) {
          toast.error('Received quantity must be greater than 0');
          return;
        }
        const remaining = coerceQty3(detail.remainingQuantity ?? detail.quantity);
        if (resolvedQty > remaining) {
          toast.error(
            `Received quantity cannot exceed remaining quantity (${formatQty3(remaining)})`,
          );
          return;
        }
      } else {
        if (resolvedQty < 0) {
          toast.error('Received quantity cannot be negative');
          return;
        }
        if (grn?.type === 'PO' && resolvedQty > detail.quantity) {
          toast.error('Received quantity cannot exceed PO quantity');
          return;
        }
      }
    }

    setSavingItems(prev => ({ ...prev, [detailId]: true }));

    try {
      let storeQty: number | undefined = undefined;
      const isMachinerySave = isMachineryCategory(getRawItemCategoryName(detail.rawItem));
      const receivedQuantity = storeOnlyMode
        ? coerceQty3(detail.originalReceivedQuantity)
        : resolveQtyFromInput(
            receivedQtyInputs[detailId],
            detail.receivedQuantity,
            isMachinerySave ? parseMachineryQtyInput : parseQtyInput,
          );
      const lineTotalForSave = isMachinerySave
        ? (detail.lineTotal || lineAmountFromQtyRate(receivedQuantity, detail.unitPrice))
        : lineAmountFromQtyRate(receivedQuantity, detail.unitPrice);

      if (detail.isNew && grn) {
        await grnApiService.createGRNDetail({
          lineNumber: detail.lineNumber,
          quantity: detail.quantity,
          receivedQuantity,
          unitPrice: detail.unitPrice,
          lineTotal: lineTotalForSave,
          qualityCheck: detail.qualityCheck || '',
          remarks: detail.remarks || '',
          heatNo: '',
          supplierHeatNo: detail.supplierHeatNo || '',
          nop: detail.nop,
          branchId: detail.branchId || grn.branchId,
          storeId: detail.storeId || null,
          storeQty: detail.storeId ? receivedQuantity : undefined,
          grnId: grn.id,
          rawItemId: detail.rawItem.id,
          createdBy: detail.createdBy || grn.createdBy,
          updatedBy: detail.updatedBy || grn.updatedBy,
        });
        await loadGRNData({ silent: true });
        return;
      }

      if (detail.storeId) {
        const originalStoreId = detail.originalStoreId;
        const newStoreId = detail.storeId;
        const oldReceivedQty = detail.originalReceivedQuantity;
        const newReceivedQty = receivedQuantity;

        if (originalStoreId === null && newStoreId !== null) {
          storeQty = newReceivedQty;
        } else if (originalStoreId !== null && originalStoreId === newStoreId && !storeOnlyMode) {
          storeQty = newReceivedQty - oldReceivedQty;
        }
      }

      await grnApiService.updateGRNDetail(detail.id.toString(), {
        id: detail.id,
        lineNumber: detail.lineNumber,
        quantity: detail.quantity,
        receivedQuantity,
        unitPrice: detail.unitPrice,
        lineTotal: lineTotalForSave,
        qualityCheck: detail.qualityCheck,
        remarks: detail.remarks,
        heatNo: detail.heatNo,
        supplierHeatNo: detail.supplierHeatNo,
        nop: detail.nop,
        branchId: detail.branchId,
        storeId: detail.storeId || null,
        storeQty: storeQty,
        grnId: detail.grn.id,
        rawItemId: detail.rawItem.id,
        createdBy: detail.createdBy,
        updatedBy: detail.updatedBy
      });
      
      toast.success(storeOnlyMode ? 'Store updated successfully!' : 'Item updated successfully!');
      
      // Update the original values to reflect the changes for this item
      setGrnDetails(prev => prev.map(d => {
        if (d.id === detailId) {
          return {
            ...d,
            originalReceivedQuantity: d.receivedQuantity,
            originalStoreId: d.storeId || null,
            originalNOP: d.nop || null,
            isModified: false
          };
        }
        return d;
      }));

      // Update hasChanges based on remaining modified items
      const remainingModified = grnDetails.filter(d => d.id !== detailId && d.isModified);
      setHasChanges(remainingModified.length > 0);
      
    } catch (error) {
      if (!detail.isNew) {
        toast.error('Failed to update item');
      }
    } finally {
      setSavingItems(prev => {
        const { [detailId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate('/grn');
      }
    } else {
      navigate('/grn');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <span className="ml-2 text-gray-600">Loading GRN details...</span>
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-600">GRN not found</p>
          <Button 
            onClick={() => navigate('/grn')}
            className="mt-4 bg-primary-500 hover:bg-primary-600 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GRN List
          </Button>
        </div>
      </div>
    );
  }

  const handleRejectionClick = (detail: EditableGRNDetail) => {
    setSelectedDetailForRejection(detail);
    
    // Check if there are existing rejections for this detail (same heatNo)
    const detailRejections = allRejections.filter(rej => 
      rej.grnNumber === grn?.grnNumber &&
      rej.heatNo === detail.heatNo &&
      rej.rawItemId === detail.rawItem.id
    );
    
    // Set existing rejections (empty array if none)
    setExistingRejections(detailRejections);
    
    // If there's an existing rejection for this heatNo, populate the form with it
    if (detailRejections.length > 0) {
      const existingRejection = detailRejections[0]; // Use the first one if multiple exist
      setEditingRejection(existingRejection);
      setRejectionData({
        rejectionDate: existingRejection.rejectDate || '',
        rejectionQty: existingRejection.rejectQty?.toString() || '',
        rejectionReason: existingRejection.reason || ''
      });
    } else {
      // No existing rejection, prepare for new entry
      setEditingRejection(null);
      setRejectionData({
        rejectionDate: '',
        rejectionQty: '',
        rejectionReason: ''
      });
    }
    
    // Open the dialog
    setRejectionDialogOpen(true);
  };

  const handleSaveRejection = async () => {
    if (!selectedDetailForRejection) return;

    if (!rejectionData.rejectionDate) {
      toast.error('Please select a rejection date');
      return;
    }

    if (!rejectionData.rejectionQty || parseFloat(rejectionData.rejectionQty) <= 0) {
      toast.error('Please enter a valid rejection quantity');
      return;
    }

    if (!rejectionData.rejectionReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }

    const rejectionQty = parseFloat(rejectionData.rejectionQty);
    if (rejectionQty > selectedDetailForRejection.receivedQuantity) {
      toast.error('Rejection quantity cannot exceed received quantity');
      return;
    }

    setSavingRejection(true);
    try {
      const rejectionDto: RmRejectionRegisterDto = {
        rejectQty: rejectionQty,
        rejectDate: rejectionData.rejectionDate,
        branchId: selectedDetailForRejection.branchId,
        rawItemId: selectedDetailForRejection.rawItem.id,
        heatNo: selectedDetailForRejection.heatNo || undefined,
        reason: rejectionData.rejectionReason.trim(),
        grnNumber: selectedDetailForRejection.grn.grnNumber || undefined,
        createUser: selectedDetailForRejection.updatedBy,
        updateUser: selectedDetailForRejection.updatedBy
      };

      // If editing existing rejection, update it; otherwise create new
      if (editingRejection && editingRejection.id) {
        await rmRejectionApiService.updateRejection(editingRejection.id, rejectionDto, selectedDetailForRejection.updatedBy);
        toast.success('Rejection updated successfully');
      } else {
        await rmRejectionApiService.createRejection(rejectionDto, selectedDetailForRejection.updatedBy);
        toast.success('Rejection recorded successfully');
      }

      setRejectionDialogOpen(false);
      setRejectionData({
        rejectionDate: '',
        rejectionQty: '',
        rejectionReason: ''
      });
      setSelectedDetailForRejection(null);
      setEditingRejection(null);
      setExistingRejections([]);
      
      // Reload GRN data to get updated information
      await loadGRNData();
    } catch (error) {
      // console.error('Error saving rejection:', error);
      toast.error('Failed to save rejection');
    } finally {
      setSavingRejection(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-4">
      <div className="max-w-[95vw] mx-auto">
        {/* Document Container */}
        <Card className="border-primary-200 shadow-2xl shadow-primary-100/50">
          <CardContent className="p-0">
            {/* Header Section */}
            <div className="bg-white p-8 border-b border-primary-200">
              <div className="flex justify-between items-start mb-6">
                {/* Logo */}
                <div className="flex items-center gap-4">
                  <InBooks24Logo size="lg" variant="horizontal" />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Goods Received Note</h1>
                {storeOnlyMode && (
                  <p className="text-primary-700 font-medium">Store assignment — quantity and other fields are read-only</p>
                )}
              </div>

              {/* GRN Details */}
              <div className="flex justify-center mb-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Hash className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">GRN No.:</span>
                        <span className="font-bold text-gray-900">{grn.grnNumber}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">PO No.:</span>
                        <span className="font-bold text-gray-900">{grn.poHeader?.poNumber || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">DC No.:</span>
                        <span className="font-bold text-gray-900">{grn.dcNo || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">Received Date:</span>
                        <span className="font-bold text-gray-900">{formatDate(grn.receiveDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">GRN Create Date:</span>
                        <span className="font-bold text-gray-900">{formatDate(grn.grnDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-600">DC Date:</span>
                        <span className="font-bold text-gray-900">{grn.dcDate ? formatDate(grn.dcDate) : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company and Supplier Information (Read-only) */}
            <div className="bg-white p-8 border-b border-primary-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Received By Section */}
                <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 p-6 rounded-lg border border-primary-200">
                  <h3 className="text-lg font-bold text-primary-800 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-700" />
                    Received By
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        {branch?.branchName || 'INBOOKS24 MANUFACTURING'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">
                        {branch?.address || 'Address: 123 Manufacturing Street, Industrial Area, City - 123456'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">GSTIN: {branch?.gstin || '12ABCDE1234F1Z5'}</p>
                    </div>
                  </div>
                </div>

                {/* Supplied By Section */}
                {grn.supplier && (
                  <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 p-6 rounded-lg border border-primary-200">
                    <h3 className="text-lg font-bold text-primary-800 mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary-700" />
                      Supplied By
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="font-bold text-gray-900 text-lg">{grn.supplier.supplierName}</p>
                      </div>
                      <div>
                        <p className="text-gray-700">
                          {grn.supplier.billAddress || grn.supplier.shipAddress || 'Address not available'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-700">GSTIN: {grn.supplier.gstNo || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Editable Items Table */}
            <div className="bg-white p-8">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary-700" />
                  {storeOnlyMode ? 'Assign Store Locations' : 'Edit Received Quantities'}
                </h3>
                <p className="text-gray-600">
                  {storeOnlyMode
                    ? 'Select or update the store for each GRN line item'
                    : 'Update received quantities, or receive PO items that were not included on this GRN earlier'}
                </p>
                {hasChanges && (
                  <div className="mt-2 flex items-center gap-2 text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">You have unsaved changes</span>
                  </div>
                )}
              </div>

              {grnDetails.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableRow className="border-primary-200">
                        <TableHead className="text-left font-semibold text-gray-700">Item</TableHead>
                        <TableHead className="text-center font-semibold text-gray-700">Heat No</TableHead>
                        <TableHead className="text-center font-semibold text-gray-700">Supplier Heat No</TableHead>
                        {grn.type !== 'MANUAL' && (
                          <>
                            <TableHead className="text-center font-semibold text-gray-700">PO Qty</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Remaining Qty</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Current Received</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Unit Price</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Line Total</TableHead>
                          </>
                        )}
                        <TableHead className="text-center font-semibold text-gray-700">Store</TableHead>
                        {!storeOnlyMode && (
                          <>
                            <TableHead className="text-center font-semibold text-gray-700">New Received Qty</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Amount</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">NOP</TableHead>
                            <TableHead className="text-center font-semibold text-gray-700">Rejection Qty</TableHead>
                          </>
                        )}
                        <TableHead className="text-center font-semibold text-gray-700">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grnDetails.map((detail) => (
                        <TableRow
                          key={detail.id}
                          className={`border-gray-100 hover:bg-gray-50 ${detail.isNew ? 'bg-amber-50/60' : ''}`}
                        >
                          <TableCell className="font-medium text-gray-900">
                            <div>
                              <div className="font-semibold">{detail.rawItem?.itemName || 'N/A'}</div>
                              <div className="text-sm text-gray-500">{detail.rawItem?.categoryName || 'RAW MATERIAL'}</div>
                              {detail.isNew && (
                                <Badge variant="outline" className="mt-1 border-amber-300 text-amber-800 bg-amber-50">
                                  Not received on this GRN
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-gray-700">
                            {detail.isNew ? '—' : (detail.heatNo || 'N/A')}
                          </TableCell>
                          <TableCell className="text-center text-gray-700">
                            {detail.isNew ? (
                              <Input
                                type="text"
                                value={detail.supplierHeatNo || ''}
                                onChange={(e) => updateSupplierHeatNo(detail.id, e.target.value)}
                                className="w-28 text-center border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                                placeholder="Supplier heat"
                              />
                            ) : (
                              detail.supplierHeatNo || 'N/A'
                            )}
                          </TableCell>
                          {grn.type !== 'MANUAL' && (
                            <>
                              <TableCell className="text-center text-gray-700">
                                {formatQty3(detail.quantity)}
                              </TableCell>
                              <TableCell className="text-center text-blue-700 font-medium">
                                {formatQty3(
                                  detail.isNew
                                    ? (detail.remainingQuantity ?? detail.quantity)
                                    : Math.max((detail.quantity || 0) - (detail.originalReceivedQuantity || 0), 0)
                                )}
                              </TableCell>
                              <TableCell className="text-center text-gray-700">
                                {formatQty3(detail.originalReceivedQuantity)}
                              </TableCell>
                              <TableCell className="text-center text-gray-700">
                                ₹{detail.unitPrice?.toLocaleString() || '0'}
                              </TableCell>
                              <TableCell className="text-center font-semibold text-gray-900">
                                ₹{formatIndianAmount(
                                  detail.lineTotal ||
                                    lineAmountFromQtyRate(detail.receivedQuantity || 0, detail.unitPrice || 0),
                                )}
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-center text-gray-700">
                            {storeOnlyMode || !detail.storeId ? (
                              <Select
                                value={detail.storeId?.toString() || ''}
                                onValueChange={(value) => updateStore(detail.id, parseInt(value))}
                              >
                                <SelectTrigger className="w-40 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                                  <SelectValue placeholder="Select store" />
                                </SelectTrigger>
                                <SelectContent>
                                  {stores.map((store) => (
                                    <SelectItem key={store.id} value={store.id.toString()}>
                                      {store.storeName}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="font-medium">
                                {stores.find(s => s.id === detail.storeId)?.storeName || 'N/A'}
                              </span>
                            )}
                          </TableCell>
                          {!storeOnlyMode && (
                            <>
                          <TableCell className="text-center">
                            {(() => {
                              const isMachinery = isMachineryCategory(getRawItemCategoryName(detail.rawItem));
                              const qtyOk = isMachinery ? isPartialMachineryQtyInput : isPartialQtyInput;
                              const parseQty = isMachinery ? parseMachineryQtyInput : parseQtyInput;
                              const formatQty = isMachinery ? formatMachineryQty : formatQty3;
                              return (
                            <div className="flex justify-center">
                              <Input
                                type="text"
                                value={
                                  receivedQtyInputs[detail.id] ??
                                  (detail.receivedQuantity
                                    ? formatQty(detail.receivedQuantity)
                                    : '')
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (!qtyOk(value)) return;
                                  setReceivedQtyInputs(prev => ({ ...prev, [detail.id]: value }));
                                  if (value && !value.endsWith('.')) {
                                    updateQuantity(detail.id, parseQty(value));
                                  } else if (value === '' || value === '.') {
                                    updateQuantity(detail.id, 0);
                                  }
                                }}
                                onBlur={(e) => {
                                  updateQuantity(detail.id, parseQty(e.target.value.trim() || '0'));
                                  setReceivedQtyInputs(prev => {
                                    const { [detail.id]: _, ...rest } = prev;
                                    return rest;
                                  });
                                }}
                                className={`${isMachinery ? 'w-32' : 'w-24'} text-center border-primary-200 focus:border-primary-400 focus:ring-primary-400`}
                                placeholder={isMachinery ? '0.0000000000' : '0.000'}
                                title={
                                  isMachinery
                                    ? 'Enter qty — amount will be calculated'
                                    : undefined
                                }
                              />
                            </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const isMachinery = isMachineryCategory(getRawItemCategoryName(detail.rawItem));
                              if (!isMachinery) {
                                return <span className="text-gray-400 text-sm">—</span>;
                              }
                              const displayAmount =
                                detail.lineTotal ||
                                lineAmountFromQtyRate(
                                  detail.receivedQuantity || 0,
                                  detail.unitPrice || 0,
                                );
                              return (
                                <div className="flex justify-center">
                                  <Input
                                    type="text"
                                    value={
                                      amountInputs[detail.id] ??
                                      (displayAmount ? displayAmount.toFixed(2) : '')
                                    }
                                    onChange={(e) => {
                                      let value = e.target.value.replace(/,/g, '');
                                      if (/^0+[1-9]/.test(value)) {
                                        value = value.replace(/^0+/, '');
                                      }
                                      if (!isPartialMoneyInput(value)) return;
                                      setAmountInputs((prev) => ({ ...prev, [detail.id]: value }));
                                      if (value && !value.endsWith('.')) {
                                        updateAmount(detail.id, parseMoneyInput(value));
                                      } else if (value === '' || value === '.') {
                                        updateAmount(detail.id, 0);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      updateAmount(
                                        detail.id,
                                        parseMoneyInput(e.target.value.replace(/,/g, '').trim() || '0'),
                                      );
                                      setAmountInputs((prev) => {
                                        const { [detail.id]: _, ...rest } = prev;
                                        return rest;
                                      });
                                    }}
                                    className="w-28 text-center border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-amber-50/40"
                                    placeholder="0.00"
                                    title="Enter amount — qty will be calculated"
                                  />
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Input
                                type="number"
                                value={detail.nop || ''}
                                onChange={(e) => updateNOP(detail.id, parseFloat(e.target.value) || 0)}
                                className="w-24 text-center border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-gray-700">
                            {formatQty3(rejectionQuantities[detail.id])}
                          </TableCell>
                            </>
                          )}
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                onClick={() => handleSaveItem(detail.id)}
                                disabled={savingItems[detail.id] || !detail.isModified}
                                size="sm"
                                className="bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed p-2"
                                title={storeOnlyMode ? 'Save store' : 'Save'}
                              >
                                {savingItems[detail.id] ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                              {!storeOnlyMode && !detail.isNew && (
                              <Button
                                onClick={() => handleRejectionClick(detail)}
                                disabled={savingItems[detail.id]}
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 p-2"
                                title="Record RM Rejection"
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-medium mb-2">No Items Found</p>
                  <p className="text-sm">No items have been added to this GRN yet, and no remaining PO items were found.</p>
                </div>
              )}

              {/* Summary */}
              {grnDetails.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className={`grid gap-6 text-sm ${grn.type === 'MANUAL' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="text-right">
                        <span className="text-gray-600">Total Quantity:</span>
                        <span className="ml-2 font-bold text-gray-900">
                          {formatQty3(grnDetails.reduce((sum, detail) => sum + (detail.receivedQuantity || 0), 0))}
                        </span>
                      </div>
                      {grn.type !== 'MANUAL' && (
                        <div className="text-right">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="ml-2 font-bold text-gray-900">
                            ₹{formatIndianAmount(
                              grnDetails.reduce(
                                (sum, detail) =>
                                  sum + lineAmountFromQtyRate(detail.receivedQuantity || 0, detail.unitPrice || 0),
                                0,
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p>Status: <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Editing</Badge></p>
                  <p className="mt-1">Received By: {grn.receivedBy}</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Created: {formatDate(grn.createdAt)}</p>
                  <p className="mt-1">Last Updated: {formatDate(grn.updatedAt)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialogOpen} onOpenChange={setRejectionDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {existingRejections.length > 0 ? 'Edit Rejection' : 'Record RM Rejection'}
            </DialogTitle>
            <DialogDescription>
              {existingRejections.length > 0 
                ? `Edit rejection details for ${selectedDetailForRejection?.rawItem?.itemName || 'this item'}`
                : `Enter rejection details for ${selectedDetailForRejection?.rawItem?.itemName || 'this item'}`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Rejection Form - Edit existing or create new */}
            <div>
              {selectedDetailForRejection?.heatNo && (
                <div className="mb-4">
                  <Label className="text-sm font-semibold text-gray-700">Heat No: {selectedDetailForRejection.heatNo}</Label>
                </div>
              )}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rejectionDate">Rejection Date *</Label>
                  <Input
                    id="rejectionDate"
                    type="date"
                    value={rejectionData.rejectionDate}
                    onChange={(e) => setRejectionData(prev => ({ ...prev, rejectionDate: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rejectionQty">Rejection Quantity *</Label>
                  <Input
                    id="rejectionQty"
                    type="number"
                    value={rejectionData.rejectionQty}
                    onChange={(e) => setRejectionData(prev => ({ ...prev, rejectionQty: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    min="0"
                    max={selectedDetailForRejection?.receivedQuantity || 0}
                    step="0.01"
                    placeholder="Enter rejection quantity"
                    required
                  />
                  {selectedDetailForRejection && (
                    <p className="text-xs text-gray-500">
                      Max: {formatQty3(selectedDetailForRejection?.receivedQuantity)}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rejectionReason">Rejection Reason *</Label>
                  <Textarea
                    id="rejectionReason"
                    value={rejectionData.rejectionReason}
                    onChange={(e) => setRejectionData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    placeholder="Enter rejection reason"
                    rows={4}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectionDialogOpen(false);
                setRejectionData({
                  rejectionDate: '',
                  rejectionQty: '',
                  rejectionReason: ''
                });
                setSelectedDetailForRejection(null);
                setExistingRejections([]);
                setEditingRejection(null);
              }}
              disabled={savingRejection}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveRejection}
              disabled={savingRejection}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {savingRejection ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingRejection ? 'Update Rejection' : 'Save Rejection'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
